import { useState } from 'react';
import { Pause, Pencil, Play, Plus } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { DatePicker, civilDateToInstant, instantToCivilDate } from '@/components/ui/date-picker';
import { toast } from '@/stores/toast-store';
import { usePermissions } from '@/hooks/use-permissions';
import { formatCurrency, formatShortDate } from '@/lib/utils';
import {
  useCreateSpendReward,
  useSetSpendRewardActive,
  useSpendRewards,
  useUpdateSpendReward,
  type SpendReward,
  type SpendRewardInput,
} from '@/hooks/use-spend-rewards';

/** The most a reward can be worth. pos-backend refuses more (it catches typos). */
const MAX_REWARD = 500;

// Dates are picked as calendar days in the browser's own zone, like the coupon
// dialog's: a "from" day is saved as its first moment, a "to" day as its last.
const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

function startOfDay(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function endOfDay(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999);
}

function dayOf(iso: string): string {
  return instantToCivilDate(new Date(iso), LOCAL_TZ);
}

function dateRange(from: string, to: string): string {
  return `${formatShortDate(from)} – ${formatShortDate(to)}`;
}

type RewardStatus = 'Scheduled' | 'Earning' | 'Paused' | 'Ended';

/** Where a reward is in its earning dates. Pausing only stops new codes, so a
 *  reward whose earning dates are over has ended either way. */
function rewardStatus(r: SpendReward, now = Date.now()): RewardStatus {
  if (now > new Date(r.earnTo).getTime()) return 'Ended';
  if (!r.isActive) return 'Paused';
  if (now < new Date(r.earnFrom).getTime()) return 'Scheduled';
  return 'Earning';
}

const STATUS_VARIANT: Record<RewardStatus, BadgeProps['variant']> = {
  Scheduled: 'info',
  Earning: 'success',
  Paused: 'warning',
  Ended: 'default',
};

function errorMessage(err: unknown, fallback: string): string {
  return (err as { error?: { message?: string } })?.error?.message || fallback;
}

/**
 * The spend rewards offered at one location, inside its Store Settings card.
 * Shown only where a super admin has turned store credit on (pos-backend
 * refuses rewards elsewhere) and to users holding promotions.view; adding
 * needs promotions.create, editing and pausing promotions.edit. Saved as
 * soon as they change, not by Save & Push to POS.
 */
export function SpendRewards({ locationId }: { locationId: string }) {
  const permissions = usePermissions();
  const canCreate = permissions.has('promotions.create');
  const canEdit = permissions.has('promotions.edit');
  const rewardsQuery = useSpendRewards(locationId);
  const setActive = useSetSpendRewardActive();
  // null: closed. { reward: null }: adding.
  const [dialog, setDialog] = useState<{ reward: SpendReward | null } | null>(null);
  const rewards = rewardsQuery.data?.data ?? [];

  const togglePaused = (r: SpendReward) => {
    setActive.mutate(
      { id: r.id, active: !r.isActive },
      {
        onSuccess: () => toast.success(r.isActive ? `${r.name} paused` : `${r.name} resumed`),
        onError: (err) => toast.error(errorMessage(err, 'Could not update the reward')),
      },
    );
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        An order over the amount during the earning dates gets a reward code printed on its receipt. The customer brings
        it back for money off an order during the use dates. One reward per order. The amount is before tax, and gift
        cards on the order don&apos;t count toward it. Rewards save as soon as you add or change them.
      </p>

      {rewardsQuery.isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : rewardsQuery.isError ? (
        <p className="text-xs text-red-500">
          {errorMessage(rewardsQuery.error, 'Could not load the rewards for this location.')}
        </p>
      ) : rewards.length === 0 ? (
        <p className="rounded border border-dashed border-border p-3 text-xs text-muted-foreground">
          No spend rewards at this location yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {rewards.map((r) => {
            const status = rewardStatus(r);
            return (
              <li key={r.id} className="rounded-md border border-border p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-medium text-foreground">{r.name}</span>
                      <Badge variant={STATUS_VARIANT[status]}>{status}</Badge>
                    </div>
                    <div className="mt-0.5 text-xs text-foreground">
                      Spend over {formatCurrency(r.minOrderTotal)} → {formatCurrency(r.rewardAmount)} off
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setDialog({ reward: r })}
                        aria-label={`Edit ${r.name}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </Button>
                      {status !== 'Ended' && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => togglePaused(r)}
                          disabled={setActive.isPending}
                          aria-label={`${r.isActive ? 'Pause' : 'Resume'} ${r.name}`}
                        >
                          {r.isActive ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                          {r.isActive ? 'Pause' : 'Resume'}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
                  <div>
                    <dt className="inline text-muted-foreground">Earn: </dt>
                    <dd className="inline text-foreground">{dateRange(r.earnFrom, r.earnTo)}</dd>
                  </div>
                  <div>
                    <dt className="inline text-muted-foreground">Use: </dt>
                    <dd className="inline text-foreground">{dateRange(r.redeemFrom, r.redeemTo)}</dd>
                  </div>
                </dl>
                <div className="mt-1.5 text-[11px] text-muted-foreground">
                  {r.issued} issued · {r.redeemed} redeemed · {r.expired} expired · {formatCurrency(r.discountGiven)}{' '}
                  given
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {canCreate && (
        <Button type="button" size="sm" variant="secondary" onClick={() => setDialog({ reward: null })}>
          <Plus className="h-3.5 w-3.5" />
          Add reward
        </Button>
      )}

      {dialog && (
        <SpendRewardDialog
          // Remount per reward so the form starts from the one being edited.
          key={dialog.reward?.id ?? 'new'}
          reward={dialog.reward}
          locationId={locationId}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}

interface RewardForm {
  name: string;
  minOrderTotal: string;
  rewardAmount: string;
  /** Calendar days, 'YYYY-MM-DD' ('' until picked). */
  earnFrom: string;
  earnTo: string;
  redeemFrom: string;
  redeemTo: string;
}

type FormErrors = Partial<Record<keyof RewardForm, string>>;

const EMPTY_FORM: RewardForm = {
  name: '',
  minOrderTotal: '',
  rewardAmount: '',
  earnFrom: '',
  earnTo: '',
  redeemFrom: '',
  redeemTo: '',
};

function formFromReward(r: SpendReward): RewardForm {
  return {
    name: r.name,
    minOrderTotal: String(r.minOrderTotal),
    rewardAmount: String(r.rewardAmount),
    earnFrom: dayOf(r.earnFrom),
    earnTo: dayOf(r.earnTo),
    redeemFrom: dayOf(r.redeemFrom),
    redeemTo: dayOf(r.redeemTo),
  };
}

/** The checks pos-backend makes, so a bad reward is caught before it is sent. */
function validate(f: RewardForm): FormErrors {
  const errors: FormErrors = {};
  if (!f.name.trim()) errors.name = 'Enter a name';
  const min = Number(f.minOrderTotal);
  if (f.minOrderTotal.trim() === '' || !Number.isFinite(min) || min <= 0) {
    errors.minOrderTotal = 'Enter an amount more than $0';
  }
  const reward = Number(f.rewardAmount);
  if (f.rewardAmount.trim() === '' || !Number.isFinite(reward) || reward <= 0 || reward > MAX_REWARD) {
    errors.rewardAmount = `Enter an amount more than $0, up to $${MAX_REWARD}`;
  }
  for (const key of ['earnFrom', 'earnTo', 'redeemFrom', 'redeemTo'] as const) {
    if (!f[key]) errors[key] = 'Pick a date';
  }
  if (f.earnFrom && f.earnTo && endOfDay(f.earnTo) <= startOfDay(f.earnFrom)) {
    errors.earnTo = 'Earning must end on or after the day it starts';
  }
  if (f.redeemFrom && f.redeemTo && endOfDay(f.redeemTo) <= startOfDay(f.redeemFrom)) {
    errors.redeemTo = 'Use must end on or after the day it starts';
  } else if (f.earnFrom && f.redeemTo && endOfDay(f.redeemTo) <= startOfDay(f.earnFrom)) {
    errors.redeemTo = 'Customers must be able to use it after earning starts';
  }
  return errors;
}

function SpendRewardDialog({
  reward,
  locationId,
  onClose,
}: {
  reward: SpendReward | null;
  locationId: string;
  onClose: () => void;
}) {
  const create = useCreateSpendReward();
  const update = useUpdateSpendReward();
  const [form, setForm] = useState<RewardForm>(() => (reward ? formFromReward(reward) : EMPTY_FORM));
  const [errors, setErrors] = useState<FormErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const saving = create.isPending || update.isPending;

  const set = (key: keyof RewardForm, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
    setServerError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const input: SpendRewardInput = {
      name: form.name.trim(),
      minOrderTotal: Math.round(Number(form.minOrderTotal) * 100) / 100,
      rewardAmount: Math.round(Number(form.rewardAmount) * 100) / 100,
      earnFrom: startOfDay(form.earnFrom).toISOString(),
      earnTo: endOfDay(form.earnTo).toISOString(),
      redeemFrom: startOfDay(form.redeemFrom).toISOString(),
      redeemTo: endOfDay(form.redeemTo).toISOString(),
      // A reward made here is offered at this location. One offered at other
      // stores as well keeps them.
      locationIds: reward?.locationIds.length ? reward.locationIds : [locationId],
    };
    try {
      if (reward) {
        await update.mutateAsync({ id: reward.id, input });
        toast.success(`${input.name} saved`);
      } else {
        await create.mutateAsync(input);
        toast.success(`${input.name} added`);
      }
      onClose();
    } catch (err) {
      setServerError(errorMessage(err, 'Could not save the reward'));
    }
  };

  const dateField = (key: 'earnFrom' | 'earnTo' | 'redeemFrom' | 'redeemTo', label: string) => (
    <div>
      <Label>{label}</Label>
      <DatePicker
        mode="date"
        portal
        timezone={LOCAL_TZ}
        format="MM/DD/YYYY"
        placeholder="MM/DD/YYYY"
        className="mt-1"
        value={civilDateToInstant(form[key], LOCAL_TZ)}
        onChange={(v) => set(key, instantToCivilDate(v instanceof Date ? v : null, LOCAL_TZ))}
        error={!!errors[key]}
        aria-label={label}
      />
      {errors[key] && <p className="mt-1 text-xs text-red-500">{errors[key]}</p>}
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={reward ? 'Edit spend reward' : 'Add spend reward'}
      subtitle={
        reward
          ? 'Changes apply to codes printed from now on. Codes already printed keep their amount and use dates.'
          : 'Orders over the amount during the earning dates get a code worth the reward.'
      }
      widthClass="max-w-[560px]"
      footer={
        <div className="flex items-center gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="spend-reward-form" disabled={saving}>
            {saving ? 'Saving…' : reward ? 'Save reward' : 'Add reward'}
          </Button>
        </div>
      }
    >
      <form id="spend-reward-form" onSubmit={handleSubmit} noValidate className="space-y-4">
        <div>
          <Label htmlFor="spend-reward-name">Name</Label>
          <Input
            id="spend-reward-name"
            className="mt-1"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="Spend $50, get $5"
            maxLength={120}
            error={!!errors.name}
            aria-invalid={!!errors.name}
          />
          {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="spend-reward-min">Minimum order total ($)</Label>
            <Input
              id="spend-reward-min"
              className="mt-1"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              selectOnFocus
              value={form.minOrderTotal}
              onChange={(e) => set('minOrderTotal', e.target.value)}
              placeholder="50.00"
              error={!!errors.minOrderTotal}
              aria-invalid={!!errors.minOrderTotal}
            />
            {errors.minOrderTotal ? (
              <p className="mt-1 text-xs text-red-500">{errors.minOrderTotal}</p>
            ) : (
              <p className="mt-1 text-[11px] text-muted-foreground">Before tax, gift cards left out.</p>
            )}
          </div>
          <div>
            <Label htmlFor="spend-reward-amount">Reward amount ($)</Label>
            <Input
              id="spend-reward-amount"
              className="mt-1"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              max={MAX_REWARD}
              selectOnFocus
              value={form.rewardAmount}
              onChange={(e) => set('rewardAmount', e.target.value)}
              placeholder="5.00"
              error={!!errors.rewardAmount}
              aria-invalid={!!errors.rewardAmount}
            />
            {errors.rewardAmount ? (
              <p className="mt-1 text-xs text-red-500">{errors.rewardAmount}</p>
            ) : (
              <p className="mt-1 text-[11px] text-muted-foreground">Up to ${MAX_REWARD}.</p>
            )}
          </div>
        </div>

        <div>
          <div className="mb-1 text-xs font-semibold text-foreground">Earning dates</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {dateField('earnFrom', 'Earn from')}
            {dateField('earnTo', 'Earn to')}
          </div>
        </div>

        <div>
          <div className="mb-1 text-xs font-semibold text-foreground">Use dates</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {dateField('redeemFrom', 'Use from')}
            {dateField('redeemTo', 'Use to')}
          </div>
        </div>

        {serverError && <p className="text-xs text-red-500">{serverError}</p>}
      </form>
    </Modal>
  );
}
